import { useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function AuthPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const getFirebaseError = (error) => {
    switch (error.code) {
      case "auth/email-already-in-use":
        return "Bu email zaten kayıtlı.";
      case "auth/invalid-email":
        return "Geçersiz email adresi.";
      case "auth/weak-password":
        return "Şifre en az 6 karakter olmalıdır.";
      case "auth/user-not-found":
        return "Bu email ile kayıtlı kullanıcı bulunamadı.";
      case "auth/wrong-password":
        return "Şifre hatalı.";
      case "auth/invalid-credential":
        return "E-posta veya şifre hatalı.";
      case "auth/too-many-requests":
        return "Çok fazla hatalı deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.";
      case "auth/network-request-failed":
        return "Bağlantı hatası. İnternet bağlantınızı kontrol edin.";
      default:
        return "Bir hata oluştu. Lütfen tekrar deneyin.";
    }
  };

  // 🔐 LOGIN
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        form.email,
        form.password
      );

      const user = userCredential.user;

      showToast("success", "Başarıyla giriş yapıldı 🎉");

      setTimeout(() => navigate("/"), 1200);
    } catch (error) {
      showToast("error", getFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  // 📝 REGISTER
  const handleRegister = async (e) => {
    e.preventDefault();

    if (form.password !== form.confirmPassword) {
      showToast("error", "Şifreler eşleşmiyor.");
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.password
      );

      const user = userCredential.user;

      await updateProfile(user, {
        displayName: form.name,
      });

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        name: form.name,
        email: form.email,
        role: "user",
        createdAt: serverTimestamp(),
      });

      await sendEmailVerification(user);

      showToast(
        "success",
        "Hesap oluşturuldu 🎉 Lütfen email adresinizi doğrulayın."
      );

      await auth.signOut();

      setTimeout(() => setIsRegister(false), 1500);
    } catch (error) {
      showToast("error", getFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  // 🔁 RESET
  const handleForgotPassword = async () => {
    if (!form.email) {
      showToast("error", "Lütfen email adresini gir.");
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, form.email);
      showToast("success", "Şifre sıfırlama maili gönderildi ✅");
    } catch (error) {
      showToast("error", getFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className={`fixed top-6 right-6 px-6 py-3 rounded-2xl shadow-xl text-white z-50 ${
              toast.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        layout
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
          {isRegister ? "Kayıt Ol" : "Giriş Yap"}
        </h1>

        <AnimatePresence mode="wait">
          <motion.form
            key={isRegister}
            initial={{ opacity: 0, x: isRegister ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRegister ? -50 : 50 }}
            transition={{ duration: 0.4 }}
            onSubmit={isRegister ? handleRegister : handleLogin}
            className="space-y-4"
          >
            {isRegister && (
              <input
                type="text"
                name="name"
                placeholder="Ad Soyad"
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            )}

            <input
              type="email"
              name="email"
              placeholder="Email"
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-3"
            />

            <input
              type="password"
              name="password"
              placeholder="Şifre"
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg p-3"
            />

            {!isRegister && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-sm text-pink-600"
                >
                  Şifremi unuttum
                </button>
              </div>
            )}

            {isRegister && (
              <input
                type="password"
                name="confirmPassword"
                placeholder="Şifre Tekrar"
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg p-3"
              />
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-pink-600 text-white py-3 rounded-lg"
            >
              {loading ? "İşleniyor..." : isRegister ? "Kayıt Ol" : "Giriş Yap"}
            </button>
          </motion.form>
        </AnimatePresence>

        <p className="text-center text-gray-500 mt-6">
          {isRegister ? "Zaten hesabın var mı?" : "Hesabın yok mu?"}{" "}
          <span
            onClick={() => setIsRegister(!isRegister)}
            className="text-pink-600 cursor-pointer"
          >
            {isRegister ? "Giriş Yap" : "Kayıt Ol"}
          </span>
        </p>
      </motion.div>
    </div>
  );
}